import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, downloadFile, ApiError } from "../../lib/api";
import { Icon } from "../../components/Icon";
import {
  STATUS_LABEL,
  STATUS_BADGE,
  DIRECTION_LABEL,
  shortId,
  formatDateTime,
  formatGel,
} from "../../lib/orders";
import { OrderDrawer, type OrderDrawerType } from "./OrderDrawer";
import { useFlexOrders } from "../../lib/useFlexOrders";
import { useOptionalRole } from "../../lib/role.tsx";
import {
  SHOP_ORDER_STATUS_LABELS_RU_ADMIN,
  FLEX_STATUS_LABELS_RU,
  hasPermission,
  type FlexStatus,
  type OrderStatus,
  type ShopOrderStatus,
} from "@mnp/shared";

// Ozon/WB-статусы и вкладка «Ozon/WB» удалены 2026-06-12 — модуль
// заархивирован в ветке archive/ozonwb-flow (тег pre-ozonwb-extract).
type AdminOrderStatus = OrderStatus | ShopOrderStatus;

const SHOP_STATUS_BADGE: Record<ShopOrderStatus, string> = {
  pending: "gray",
  in_review: "orange",
  rejected: "red",
  awaiting_payment: "yellow",
  paid: "blue",
  ready: "teal",
  completed: "green",
  cancelled: "red",
  // Отказ при выдаче (сканер личного QR) — карточка shop-status-flow-shared.
  refused: "red",
};

const ALL_STATUSES: AdminOrderStatus[] = [
  "draft",
  "approved",
  "has_qr",
  "picked_up",
  "in_transit",
  "at_warehouse",
  "ready",
  "delivered",
  "pending",
  "awaiting_payment",
  "paid",
  "completed",
  "cancelled",
];

const ALL_DIRECTIONS: string[] = [
  "from_rf",
  "to_rf",
  "receipt",
  "pickup",
  "packing",
  "shop",
];

interface Filters {
  statuses: AdminOrderStatus[];
  directions: string[];
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = { statuses: [], directions: [], from: "", to: "" };

type Order = {
  id: string;
  chatId: string;
  direction: string;
  status: AdminOrderStatus;
  qrCodePickup?: string | null;
  items: Array<{ name?: string; qty?: number }>;
  deliveryType: string | null;
  totalGel: number | null;
  // "preliminary" → amber-highlighted (provisional), "final"/null → plain.
  totalKind?: "preliminary" | "final" | null;
  hasHighTariff: boolean;
  createdAt: string;
  user?: {
    chatId: string;
    username: string | null;
    fullName: string | null;
    phone: string | null;
  };
};

type Tab = "all" | "active" | "done";

// Order-direction filter (E2). "shop" is surfaced to staff as «Упаковка».
type TypeTab = "all" | "flex" | "shop";

const TYPE_TABS: Array<{ key: TypeTab; label: string }> = [
  { key: "all", label: "Все" },
  { key: "flex", label: "Flex" },
  { key: "shop", label: "Упаковка" },
];

function drawerTypeFor(direction: string): OrderDrawerType | undefined {
  if (direction === "flex") return "flex";
  if (direction === "shop") return "shop";
  return undefined;
}

const ACTIVE_STATUSES: AdminOrderStatus[] = [
  "draft",
  "approved",
  "has_qr",
  "picked_up",
  "in_transit",
  "at_warehouse",
  "ready",
  "pending",
  "awaiting_payment",
  "paid",
];

function displayOrderId(order: Pick<Order, "id" | "qrCodePickup">): string {
  return order.qrCodePickup || shortId(order.id);
}

export function AllOrdersSubscreen() {
  const [tab, setTab] = useState<Tab>("all");
  const [typeTab, setTypeTab] = useState<TypeTab>("all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [openOrder, setOpenOrder] = useState<{ id: string; type?: OrderDrawerType } | null>(
    null,
  );
  const [exportError, setExportError] = useState<string | null>(null);

  // Экспорт CSV на API закрыт permission "orders:export_csv"
  // (requirePermissionHook в routes/admin/reports.ts) — роли без неё
  // получали 403 по клику; кнопку им не показываем.
  const role = useOptionalRole();
  const canExport = hasPermission(role, "orders:export_csv");

  const activeFilterCount =
    filters.statuses.length +
    filters.directions.length +
    (filters.from ? 1 : 0) +
    (filters.to ? 1 : 0);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orders"],
    queryFn: () => api<Order[]>("/admin/orders"),
  });

  // Flex orders live in a separate table/endpoint. Fetch them lazily only
  // when the Flex tab is selected so the default view stays a single call.
  const { data: flexData } = useFlexOrders({ enabled: typeTab === "flex" });
  const flexRows = Array.isArray((flexData as any)?.rows)
    ? (flexData as any).rows
    : [];

  // С PR5 сервер отдаёт только живые направления (shop + flex): legacy-таблица
  // Order на /admin/orders больше не читается (архив archive/ozonwb-flow),
  // клиентский фильтр архивных строк стал не нужен и удалён в PR6.
  const legacyOrders = useMemo(() => data ?? [], [data]);

  const orders: Order[] = useMemo(() => {
    if (typeTab === "flex") {
      return flexRows.map(
        (f: {
          id: string;
          chatId: string;
          status: FlexStatus;
          createdAt: string;
          announcedAmountGel?: string | null;
          announcedKind?: "preliminary" | "final" | null;
          displayTotalGel?: number | null;
          displayTotalKind?: "preliminary" | "final" | null;
        }): Order => ({
          id: f.id,
          chatId: String(f.chatId),
          direction: "flex",
          status: f.status as AdminOrderStatus,
          qrCodePickup: null,
          items: [],
          deliveryType: null,
          // «Итого» (₾): announced sum, else the computed preliminary/final.
          totalGel:
            f.displayTotalGel ?? (f.announcedAmountGel != null ? Number(f.announcedAmountGel) : null),
          totalKind: f.displayTotalKind ?? f.announcedKind ?? null,
          hasHighTariff: false,
          createdAt: f.createdAt,
          user: {
            chatId: String(f.chatId),
            username: null,
            fullName: null,
            phone: null,
          },
        }),
      );
    }
    if (typeTab === "shop") return legacyOrders.filter((o) => o.direction === "shop");
    return legacyOrders;
  }, [typeTab, legacyOrders, flexRows]);

  const counts = useMemo(() => {
    const all = orders.length;
    const active = orders.filter((o) => ACTIVE_STATUSES.includes(o.status)).length;
    const done = orders.filter((o) => o.status === "delivered" || o.status === "completed").length;
    return { all, active, done };
  }, [orders]);

  const filtered = useMemo(() => {
    let rows = orders;
    if (tab === "active") rows = rows.filter((o) => ACTIVE_STATUSES.includes(o.status));
    else if (tab === "done") rows = rows.filter((o) => o.status === "delivered" || o.status === "completed");
    if (filters.statuses.length > 0)
      rows = rows.filter((o) => filters.statuses.includes(o.status));
    if (filters.directions.length > 0)
      rows = rows.filter((o) => filters.directions.includes(o.direction));
    if (filters.from) {
      const f = new Date(filters.from).getTime();
      rows = rows.filter((o) => new Date(o.createdAt).getTime() >= f);
    }
    if (filters.to) {
      const t = new Date(filters.to).getTime() + 24 * 60 * 60 * 1000;
      rows = rows.filter((o) => new Date(o.createdAt).getTime() < t);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          (o.qrCodePickup ?? "").toLowerCase().includes(q) ||
          (o.user?.fullName ?? "").toLowerCase().includes(q) ||
          (o.user?.phone ?? "").toLowerCase().includes(q) ||
          (o.user?.username ?? "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [orders, tab, search, filters]);

  function buildExportPath(): string {
    const params = new URLSearchParams();
    if (filters.statuses.length > 0) params.set("status", filters.statuses.join(","));
    if (filters.directions.length === 1 && filters.directions[0])
      params.set("direction", filters.directions[0]);
    if (filters.from) params.set("from", new Date(filters.from).toISOString());
    if (filters.to) {
      const end = new Date(filters.to);
      end.setHours(23, 59, 59, 999);
      params.set("to", end.toISOString());
    }
    const q = params.toString();
    return `/admin/reports/orders.csv${q ? `?${q}` : ""}`;
  }

  async function onExport() {
    setExportError(null);
    try {
      await downloadFile(buildExportPath(), "orders.csv");
    } catch (e) {
      console.error(e);
      // Сервер без from/to сам берёт «последние 90 дней»; 400 здесь — это
      // явно выставленный фильтром период длиннее серверного капа.
      if (e instanceof ApiError && e.status === 400) {
        setExportError("Период экспорта не может превышать 90 дней — сузьте фильтр дат");
      } else if (e instanceof ApiError && e.status === 429) {
        setExportError("Лимит выгрузок исчерпан — попробуйте позже");
      } else {
        setExportError("Не удалось скачать CSV");
      }
    }
  }

  return (
    <>
      <div className="content">
        <div className="toolbar">
          <div className="seg" data-testid="order-type-tabs">
            {TYPE_TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`type-tab-${t.key}`}
                data-active={typeTab === t.key}
                onClick={() => setTypeTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="seg">
            <button data-active={tab === "all"} onClick={() => setTab("all")}>
              Все · {counts.all}
            </button>
            <button data-active={tab === "active"} onClick={() => setTab("active")}>
              Активные · {counts.active}
            </button>
            <button data-active={tab === "done"} onClick={() => setTab("done")}>
              Завершённые · {counts.done}
            </button>
          </div>
          <div className="search-inline">
            <Icon name="search" size={14} />
            <input
              placeholder="Поиск по ID, клиенту, телефону…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            className="btn-admin"
            type="button"
            onClick={() => setFilterOpen(true)}
          >
            <Icon name="filter" size={14} /> Фильтры
            {activeFilterCount > 0 && (
              <span className="badge-a teal" style={{ marginLeft: 6, padding: "2px 8px", fontSize: 11 }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          {canExport && (
            <button className="btn-admin" type="button" onClick={onExport}>
              <Icon name="download" size={14} /> Экспорт
            </button>
          )}
          {exportError && <span className="text-red-500 text-xs">{exportError}</span>}
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ID</th>
                <th>Клиент</th>
                <th>Направление</th>
                <th className="num">Товары</th>
                <th className="num">Итого</th>
                <th>Статус</th>
                <th>Создан</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} style={{ padding: 40, textAlign: "center", color: "var(--mnp-fg-3)" }}>
                    Загрузка…
                  </td>
                </tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 40, textAlign: "center", color: "var(--mnp-fg-3)" }}>
                    Заказов пока нет
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`order-row-${r.id}`}
                  onClick={() => setOpenOrder({ id: r.id, type: drawerTypeFor(r.direction) })}
                  style={{ cursor: "pointer" }}
                >
                  <td className="mono">{displayOrderId(r)}</td>
                  <td>
                    <div className="bold">{r.user?.fullName ?? "—"}</div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {r.user?.phone ?? r.user?.username ?? r.chatId}
                    </div>
                  </td>
                  <td>{directionLabel(r.direction)}</td>
                  <td className="num tnum">{r.items?.length ?? 0}</td>
                  <td className="num tnum bold">
                    {r.totalGel != null && r.totalKind === "preliminary" ? (
                      <span
                        style={{ color: "var(--mnp-warning)" }}
                        title="Предварительная цена (не итоговая)"
                      >
                        {formatGel(r.totalGel)}
                      </span>
                    ) : (
                      formatGel(r.totalGel)
                    )}
                  </td>
                  <td>
                    <span className={"badge-a " + statusBadge(r.status)}>
                      {r.hasHighTariff && "⚠ "}
                      {statusLabel(r.status)}
                    </span>
                  </td>
                  <td className="muted">{formatDateTime(r.createdAt)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions">
                      <button
                        title="Открыть"
                        aria-label="Открыть заказ"
                        onClick={() =>
                          setOpenOrder({ id: r.id, type: drawerTypeFor(r.direction) })
                        }
                      >
                        <Icon name="eye" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isLoading && filtered.length > 0 && (
            <div className="pagination">
              <span>Показаны 1–{filtered.length} из {filtered.length}</span>
              <div className="spacer" />
            </div>
          )}
        </div>
      </div>

      {openOrder && (
        <OrderDrawer
          id={openOrder.id}
          type={openOrder.type}
          onClose={() => setOpenOrder(null)}
        />
      )}

      {filterOpen && (
        <FilterDrawer
          value={filters}
          onApply={(f) => {
            setFilters(f);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </>
  );
}

function FilterDrawer({
  value,
  onApply,
  onClose,
}: {
  value: Filters;
  onApply: (f: Filters) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Filters>(value);

  function toggleStatus(s: AdminOrderStatus) {
    setDraft((d) =>
      d.statuses.includes(s)
        ? { ...d, statuses: d.statuses.filter((x) => x !== s) }
        : { ...d, statuses: [...d.statuses, s] },
    );
  }
  function toggleDirection(s: string) {
    setDraft((d) =>
      d.directions.includes(s)
        ? { ...d, directions: d.directions.filter((x) => x !== s) }
        : { ...d, directions: [...d.directions, s] },
    );
  }

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <h2>Фильтры</h2>
          <div className="grow" />
          <button className="btn-admin" aria-label="Закрыть фильтры" onClick={onClose}>
            <Icon name="close" size={14} />
          </button>
        </div>
        <div className="drawer-body" style={{ display: "grid", gap: 18 }}>
          <div>
            <p className="panel-title" style={{ marginBottom: 10 }}>
              Статус
            </p>
            <div className="f-row" style={{ gap: 8, flexWrap: "wrap" }}>
              {ALL_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={"badge-a " + (draft.statuses.includes(s) ? statusBadge(s) : "gray")}
                  style={{
                    cursor: "pointer",
                    border: 0,
                    fontSize: 12,
                    padding: "6px 12px",
                    opacity: draft.statuses.includes(s) ? 1 : 0.5,
                  }}
                  onClick={() => toggleStatus(s)}
                >
                  {statusLabel(s)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="panel-title" style={{ marginBottom: 10 }}>
              Направление
            </p>
            <div className="f-row" style={{ gap: 8, flexWrap: "wrap" }}>
              {ALL_DIRECTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={"badge-a " + (draft.directions.includes(d) ? "teal" : "gray")}
                  style={{
                    cursor: "pointer",
                    border: 0,
                    fontSize: 12,
                    padding: "6px 12px",
                    opacity: draft.directions.includes(d) ? 1 : 0.5,
                  }}
                  onClick={() => toggleDirection(d)}
                >
                  {directionLabel(d)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="panel-title" style={{ marginBottom: 10 }}>
              Период создания
            </p>
            <div className="f-row" style={{ gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
                  С
                </label>
                <input
                  className="input-admin"
                  type="date"
                  style={{ width: "100%" }}
                  value={draft.from}
                  onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
                  По
                </label>
                <input
                  className="input-admin"
                  type="date"
                  style={{ width: "100%" }}
                  value={draft.to}
                  onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="f-row" style={{ justifyContent: "space-between", marginTop: 8 }}>
            <button
              className="btn-admin"
              type="button"
              onClick={() => setDraft(EMPTY_FILTERS)}
            >
              Сбросить
            </button>
            <button
              className="btn-admin primary"
              type="button"
              onClick={() => onApply(draft)}
            >
              Применить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function statusLabel(status: AdminOrderStatus): string {
  return (
    (STATUS_LABEL as Partial<Record<AdminOrderStatus, string>>)[status] ??
    SHOP_ORDER_STATUS_LABELS_RU_ADMIN[status as ShopOrderStatus] ??
    (FLEX_STATUS_LABELS_RU as Partial<Record<string, string>>)[status] ??
    status
  );
}

function statusBadge(status: AdminOrderStatus): string {
  return (
    (STATUS_BADGE as Partial<Record<AdminOrderStatus, string>>)[status] ??
    SHOP_STATUS_BADGE[status as ShopOrderStatus] ??
    "gray"
  );
}

function directionLabel(direction: string): string {
  if (direction === "shop") return "Магазин";
  if (direction === "flex") return "Flex";
  return DIRECTION_LABEL[direction] ?? direction;
}
